import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ObservationUnitsService } from './service/observationUnits.service';
import { ObservationUnit } from './model/observationUnit';
import * as L from 'leaflet';
import * as turf from '@turf/turf';
import * as d3 from 'd3';
import { convertGeoJSONStringToPolygon, convertTurfToPolygon } from './shape/shape-utils';
import { Block } from './shape/block';
import { Study } from './shape/study';
import { Plot } from './shape/plot';
import { FieldMapContext } from './field-map-context.model';
import { Field } from './shape/field';
import { WorkbenchLocationService } from '../shared/workbench-location/service/workbench-location.service';
import { LocationTypeEnum } from '../shared/workbench-location/model/location-type.enum';
import { Location } from '../shared/workbench-location/model/location';
import { LocationStudyService } from '../shared/workbench-location/service/location-study.service';
import { LocationStudy } from '../shared/workbench-location/model/location-study.model';
import { FieldmapInfo, FieldmapService } from './service/fieldmap.service';

@Injectable()
export class StudyShapeGenerator {

    private splitPlot_memo: { [key: string]: turf.helpers.Feature<turf.helpers.Polygon>[] };

    constructor(protected httpClient: HttpClient,
                private observationUnitsService: ObservationUnitsService,
                private workbenchLocationService: WorkbenchLocationService,
                private locationStudyService: LocationStudyService,
                private fieldMapContext: FieldMapContext,
                private fielmapService: FieldmapService) {
        this.initialize();
    }

    initialize() {
    }

    async loadField(workbenchLocation: Location, map: L.Map, fieldsDrawingLayer: L.FeatureGroup<any>): Promise<Field | null> {

        // if the field is already loaded and drawn, just return the existing one.
        if (this.fieldMapContext.fieldsMap.has(workbenchLocation.id.toString())) {
            return this.fieldMapContext.fieldsMap.get(workbenchLocation.id.toString());
        }

        if (workbenchLocation.geometry) {
            const polygon = convertGeoJSONStringToPolygon(workbenchLocation.geometry);
            const field = new Field({
                map,
                id: workbenchLocation.id.toString(),
                name: workbenchLocation.name,
                polygon,
                fieldsDrawingLayer,
                fieldMapContext: this.fieldMapContext
            });
            field.draw();
            field.reset();
            field.select();

            const blockLocations = await this.workbenchLocationService.searchLocations({
                locationTypeIds: [LocationTypeEnum.BLOCK],
                parentLocIds: [workbenchLocation.id]
            }, null).toPromise();

            if (blockLocations.body) {
                for (const workbenchLocation1 of blockLocations.body) {
                    await this.loadBlock(workbenchLocation1, map, field);
                }
            }

            return field;
        }
        return null;
    }

    async loadBlock(workbenchLocation: Location, map: L.Map, parentField: Field): Promise<Block> {
        if (workbenchLocation.geometry) {
            const polygon = convertGeoJSONStringToPolygon(workbenchLocation.geometry);
            const block = new Block({
                map,
                id: workbenchLocation.id.toString(),
                name: workbenchLocation.name,
                polygon,
                parent: parentField,
                fieldMapContext: this.fieldMapContext
            });
            block.draw();
            block.reset();

            const locationStudies = await this.locationStudyService.getByLocationId(parseInt(block.id, 10)).toPromise();
            if (locationStudies) {
                for (const locationStudy of locationStudies) {
                    const study = await this.loadStudy(locationStudy, map, block);
                    if (study) {
                        study.draw();
                    }
                }
            }

            return block;
        }
        return null;
    }

    async loadStudyWithoutParentBlock(fieldmapInfo: FieldmapInfo, map: L.Map): Promise<Study> {

        const study = new Study({
            map,
            id: '',
            name: '',
            parent: null,
            polygon: L.polygon([]),
            fieldMapContext: this.fieldMapContext,
            crop: fieldmapInfo.crop,
            environmentNumber: fieldmapInfo.instanceNumber.toString()
        });

        const observationUnitListResponse =
            await this.observationUnitsService.observationunitsGet(fieldmapInfo.crop,
                { studyDbId: fieldmapInfo.instanceId.toString() }, 'body').toPromise();

        if (observationUnitListResponse.result && observationUnitListResponse.result.data) {

            const observationUnits = observationUnitListResponse.result.data;

            study.name = observationUnits[0].studyName;
            study.id = observationUnits[0].studyDbId;

            const hasGeoCoordinates = observationUnits.some((observationUnit) => observationUnit.observationUnitPosition.geoCoordinates !== null);
            if (hasGeoCoordinates) {
                observationUnitListResponse.result.data.forEach((observationUnit: ObservationUnit) => {
                    if (observationUnit.observationUnitPosition && observationUnit.observationUnitPosition.geoCoordinates) {
                        const plotGeometry = observationUnit.observationUnitPosition.geoCoordinates as turf.Feature;
                        plotGeometry.properties = { observationUnit: { observationUnitDbId: observationUnit.observationUnitDbId } };
                        this.generatePlotShape(map, study, observationUnit, plotGeometry);
                    }
                });
            } else {
                return null;
            }

        }

        return study;
    }

    async loadStudy(locationStudy: LocationStudy, map: L.Map, block: Block): Promise<Study> {

        let environmentNumber: string;
        const fieldMapResult = await this.fielmapService.searchFieldMapInfo(locationStudy.cropName, {
            instanceId: locationStudy.ndGeolocationId,
            withGeoJsonOnly: true
        }).toPromise();
        if (fieldMapResult && fieldMapResult.results.length > 0) {
            environmentNumber = fieldMapResult.results[0].instanceNumber.toString();
        } else {
            return null;
        }

        const study = new Study({
            map,
            id: '',
            name: '',
            parent: block,
            polygon: L.polygon([]),
            fieldMapContext: this.fieldMapContext,
            crop: locationStudy.cropName,
            environmentNumber
        });

        const observationUnitListResponse =
            await this.observationUnitsService.observationunitsGet(locationStudy.cropName,
                { studyDbId: locationStudy.ndGeolocationId.toString() }, 'body').toPromise();

        if (observationUnitListResponse.result && observationUnitListResponse.result.data) {

            const observationUnits = observationUnitListResponse.result.data;

            study.name = observationUnits[0].studyName;
            study.id = observationUnits[0].studyDbId;

            const hasGeoCoordinates = observationUnits.some((observationUnit) => observationUnit.observationUnitPosition.geoCoordinates !== null);
            if (hasGeoCoordinates) {
                observationUnitListResponse.result.data.forEach((observationUnit: ObservationUnit) => {
                    if (observationUnit.observationUnitPosition && observationUnit.observationUnitPosition.geoCoordinates) {
                        const plotGeometry = observationUnit.observationUnitPosition.geoCoordinates as turf.Feature;
                        plotGeometry.properties = { observationUnit: { observationUnitDbId: observationUnit.observationUnitDbId } };
                        this.generatePlotShape(map, study, observationUnit, plotGeometry);
                    }
                });
            } else {
                return null;
            }

        }

        return study;
    }

    async generateField(fieldName: string, polygon: L.Polygon, map: L.Map, fieldsDrawingLayer: L.FeatureGroup<any>): Promise<Field> {

        const fieldLocation = await this.workbenchLocationService.createLocation({
            geometry: JSON.stringify(polygon.toGeoJSON()),
            name: fieldName,
            type: LocationTypeEnum.FIELD
        }).toPromise();

        if (fieldLocation) {
            const field = new Field({
                map,
                id: fieldLocation.id.toString(),
                name: fieldLocation.name,
                polygon,
                fieldsDrawingLayer,
                fieldMapContext: this.fieldMapContext
            });
            field.draw();
            field.reset();
            field.select();
            return field;
        }
    }

    async generateBlock(fieldName: string, polygon: L.Polygon, map: L.Map, parentField: Field) {

        const blockLocation = await this.workbenchLocationService.createLocation({
            geometry: JSON.stringify(polygon.toGeoJSON()),
            name: fieldName,
            parentLocId: parseInt(parentField.id, 10),
            type: LocationTypeEnum.BLOCK
        }).toPromise();

        if (blockLocation) {
            const block = new Block({
                map,
                id: blockLocation.id.toString(),
                name: blockLocation.name,
                polygon,
                parent: parentField,
                fieldMapContext: this.fieldMapContext
            });
            block.draw();
            block.reset();
        }
    }

    async generateStudyShape(cropName: string, map: L.Map, block: Block, studyDbId: string,
        polygon: L.Polygon, fieldMapContext: FieldMapContext, plotLengthParam = 0, plotWidthParam = 0): Promise<Study> {

        let environmentNumber: string;
        const fieldMapResult = await this.fielmapService.searchFieldMapInfo(cropName, {
            instanceId: parseInt(studyDbId, 10),
            withGeoJsonOnly: false
        }).toPromise();
        if (fieldMapResult && fieldMapResult.results.length > 0) {
            environmentNumber = fieldMapResult.results[0].instanceNumber.toString();
        } else {
            return Promise.reject();
        }

        const study = new Study({
            map,
            id: '',
            name: '',
            parent: block,
            polygon,
            fieldMapContext,
            crop: cropName,
            environmentNumber
        });

        const existingLocationStudy = await this.locationStudyService.getByLocationAndGeolocation(parseInt(block.id, 10), parseInt(studyDbId, 10)).toPromise();
        if (existingLocationStudy) {
            return Promise.reject();
        }

        const observationUnits = await this.prepareObservationUnits(studyDbId, cropName);
        if (observationUnits.length > 0) {
            study.name = observationUnits[0].studyName;
            study.id = observationUnits[0].studyDbId;

            this.generatePlots(map, study, observationUnits, fieldMapContext, plotLengthParam, plotWidthParam);

            return Promise.resolve(study);
        }

        return Promise.reject();
    }

    generatePlotShape(map: L.Map, study: Study, observationUnit: ObservationUnit, geometry: turf.helpers.Feature<any>): Plot {

        const plotPolygon: L.Polygon = convertTurfToPolygon(geometry);
        const plot = new Plot({
            map,
            id: observationUnit.observationUnitDbId,
            name: `#: <b>${this.getPlotNumber(observationUnit)}</b> Row,Col: <b>${observationUnit.observationUnitPosition.positionCoordinateX},
${observationUnit.observationUnitPosition.positionCoordinateY}</b>`,
            observationUnit,
            polygon: plotPolygon,
            parent: study,
            fieldMapContext: this.fieldMapContext,
            crop: study.crop
        });

        return plot;
    }

    private async prepareObservationUnits(studyDbId: string, cropName: string): Promise<ObservationUnit[]> {

        const observationUnitListResponse = await this.observationUnitsService.observationunitsGet(cropName, { studyDbId }, 'body').toPromise();

        if (observationUnitListResponse.result && observationUnitListResponse.result.data) {
            const observationUnits = observationUnitListResponse.result.data;
            // @ts-ignore
            const uniquePlots = Array.from(new Map(observationUnits
                // Only get the 'PLOT' observation units
                .filter((observationUnit) => observationUnit.observationUnitPosition.observationLevel.levelName.toUpperCase() === 'PLOT')
                // Make sure the plots are unique
                .map((plot) => [plot.observationUnitDbId, plot]))
                .values());

            const sortedUniquePlots = uniquePlots.sort((a, b) => {
                // Sort the plots by plotNumber
                // observationUnitPosition.observationLevel.levelCode is the plotNumber
                // @ts-ignore
                const numA = Number(a.observationUnitPosition.observationLevel.levelCode);
                // @ts-ignore
                const numB = Number(b.observationUnitPosition.observationLevel.levelCode);
                return numA - numB;
            });

            // @ts-ignore
            return Promise.resolve(sortedUniquePlots);
        }

        return Promise.resolve([]);
    }

    private generatePlots(map: L.Map, study: Study, observationUnits: ObservationUnit[], fieldMapContext: FieldMapContext, plotLengthParam = 0, plotWidthParam = 0): void {

        let plot_XY_groups: Record<string, Record<string, ObservationUnit[]>>;
        plot_XY_groups = observationUnits.reduce<Record<string, Record<string, ObservationUnit[]>>>((accumulator, plot) => {
            const col = plot.observationUnitPosition.positionCoordinateX;
            const row = plot.observationUnitPosition.positionCoordinateY;
            if (!accumulator[col]) {
                accumulator[col] = {};
            }
            if (!accumulator[col][row]) {
                accumulator[col][row] = [];
            }
            accumulator[col][row].push(plot);
            return accumulator;
        }, {});

        const boundingArea = study.polygon.toGeoJSON();
        const rotatedTurfPolygon = this.level(boundingArea);
        const bbox = turf.bbox(rotatedTurfPolygon.feature);

        const defaultPos = [bbox[0], bbox[3]];
        let plotLength = plotLengthParam / 1000;
        let plotWidth = plotWidthParam / 1000;

        const cols = Object.keys(plot_XY_groups).length,
            rows = (Object.values(plot_XY_groups).reduce((acc, col) => {
                Object.keys(col).forEach((row, i) => {
                    if (!row) {
                        return;
                    }
                    acc[i] = acc[i] + 1 || 1;
                });
                return acc;
            }, []) as any).filter((x) => x).length;
        plotLength = plotLength || turf.length(turf.lineString([[bbox[0], bbox[1]], [bbox[0], bbox[3]]])) / rows;
        plotWidth = plotWidth || turf.length(turf.lineString([[bbox[0], bbox[1]], [bbox[2], bbox[1]]])) / cols;

        const plotGeometries: turf.helpers.Feature<turf.helpers.Polygon>[] = [];
        // Use default plot shapes/positions based on X/Y positions
        for (const x in plot_XY_groups) {
            if (plot_XY_groups.hasOwnProperty(x)) {
                for (const y in plot_XY_groups[x]) {
                    if (plot_XY_groups[x].hasOwnProperty(y)) {
                        const polygon = this.defaultPlot(parseInt(y, 10) - 1, parseInt(x, 10) - 1, plotWidth, plotLength, defaultPos);
                        // if for some reason plots have the same x/y, split that x/y region
                        plot_XY_groups[x][y].forEach((observationUnit, i) => {
                            const plotGeometry = this.splitPlot(polygon, plot_XY_groups[x][y].length, i);
                            plotGeometry.properties = { observationUnit };
                            plotGeometries.push(plotGeometry);
                        });
                    }
                }
            }
        }

        let plots = turf.featureCollection(plotGeometries);
        plots = turf.transformRotate(plots, -rotatedTurfPolygon.rotation);

        plots.features.map((plot) => {
            const plotGeometry = turf.transformScale(plot, 1);
            this.generatePlotShape(map, study, plot.properties.observationUnit, plotGeometry);
        });

        // // Update the geometry of the Study polygon so that it is the same shape/area as the bounding box of all plots generated.
        // let bboxPolygon = turf.bboxPolygon(turf.bbox(plots)); // Get bounding box of the plots
        // let bboxCoords = bboxPolygon.geometry.coordinates[0]; // Get coordinates of the bounding box
        // // Convert GeoJSON coordinates to LatLng points
        // let latLngs = bboxCoords.map(coord => L.latLng(coord[1], coord[0]));
        // study.polygon.setLatLngs(latLngs);
        // study.polygon.redraw();
    }

    /**
     * Try to make the polygon vertical before calculating row and col,
     * by rotating it in an angle formed between the center and one of
     * the far ends.
     */
    private level(feature: turf.Feature<any>): { rotation: number, feature: turf.helpers.Feature<any> } {
        const cellSide = Math.sqrt(turf.area(feature)) / 1000 / 10 / 2;
        const grid = turf.pointGrid(turf.bbox(feature), cellSide, { mask: feature });
        const center = turf.getCoord(turf.centerOfMass(feature));
        const distances = grid.features.map((f) => turf.distance(center, turf.getCoord(f))).sort();
        const q3 = d3.quantile(distances, 0.75);
        const clusters = turf.clustersKmeans(turf.featureCollection(grid.features.filter((f) => {
            return turf.distance(center, turf.getCoord(f)) > q3;
        })), { numberOfClusters: 2 });
        const clusterCenters: any[] = [];
        turf.clusterEach(clusters, 'cluster', (cluster) => {
            clusterCenters.push(turf.getCoord(turf.center(cluster)));
        });
        const bearing = turf.bearing(center, this.northernmost(clusterCenters[0], clusterCenters[1]));
        const rotation = -bearing;
        return { rotation, feature: turf.transformRotate(feature, rotation) };
    }

    private defaultPlot = function(row, col, plotWidth, plotLength, defaultPos): turf.helpers.Feature<turf.helpers.Polygon, {}> {
        const o = turf.point(defaultPos);
        const tl = turf.destination(
            turf.destination(
                o,
                plotWidth * col,
                90,
                { 'units': 'kilometers' }
            ),
            plotLength * row,
            180,
            { 'units': 'kilometers' }
        );
        const br = turf.destination(
            turf.destination(
                tl,
                plotWidth,
                90,
                { 'units': 'kilometers' }
            ),
            plotLength,
            180,
            { 'units': 'kilometers' }
        );
        const tr = turf.point([tl.geometry.coordinates[0], br.geometry.coordinates[1]]);
        const bl = turf.point([br.geometry.coordinates[0], tl.geometry.coordinates[1]]);
        return turf.polygon([
            [tl, tr, br, bl, tl].map(turf.getCoord)
        ], {});
    };

    // Method definition take three arguments: polygon (a polygon), partitions (number of parts to split the polygon into), index (of the part to return)

    splitPlot(polygon: turf.helpers.Feature<turf.helpers.Polygon, {}>, partitions: number, index: number): turf.helpers.Feature<turf.helpers.Polygon> {

        // Defining splitPlot_memo as a cache object to remember
        // previously computed calculations of split polygons. This is an example of memoization.
        this.splitPlot_memo = this.splitPlot_memo || {};
        const memo_key = `(${partitions})${polygon.geometry.coordinates.join(',')}`;

        // If cached, return result from cache.
        if (this.splitPlot_memo[memo_key]) {
            return this.splitPlot_memo[memo_key][index];
        }

        // If number of partitions is less than 2,
        // return the polygon as is in an array and set it in cache.
        if (!partitions || partitions < 2) {
            return (this.splitPlot_memo[memo_key] = [polygon])[index];
        }

        // Transforming the scale of the polygon to avoid rounding errors.
        const scale_factor = 50;
        const scale_origin = turf.getCoord(turf.centroid(polygon));
        polygon = turf.transformScale(polygon, scale_factor, { 'origin': scale_origin });

        // Divides the partitions evenly into rows. If partitions is not a perfect square,
        // the last row could have fewer polygons than the others.
        const row_width = Math.ceil(Math.sqrt(partitions));
        const row_counts = [];
        for (let i = 0; i < Math.floor(partitions / row_width); i++) {
            row_counts[i] = row_width;
        }
        if (partitions % row_width) {
            row_counts[row_counts.length] = partitions % row_width;
        }

        // Creating bounding box for the polygon and expanding it slightly to avoid rounding errors.
        const polygonbbox = turf.bbox(polygon);
        polygonbbox[0] -= 0.00001;
        polygonbbox[1] -= 0.00001;
        polygonbbox[2] += 0.00001;
        polygonbbox[3] += 0.00001;

        // More setup for the grid point creation.
        // "w" is the (approximate) width of the original polygon in kilometers.
        const w = Math.sqrt(turf.area(polygon)) / 1000;
        const area = 50 + 100 * partitions;
        const grid_dist = w / Math.sqrt(area);
        const grid = turf.pointGrid(polygonbbox, grid_dist, { 'mask': polygon });

        // Preparing to split the points evenly into the partitioned polygons.
        let points = grid.features;
        const points_per_part = Math.floor(points.length / partitions);
        const row_point_counts = row_counts.map((rc) => rc * points_per_part);

        // Sorting the points by latitude.
        points = points.sort((b, a) => d3.ascending(turf.getCoord(a)[1], turf.getCoord(b)[1]));

        // Splitting the points into rows.
        let t = 0;
        const rows = [];
        row_point_counts.forEach((rpc, i) => {
            rows[i] = [];
            while (rows[i].length < rpc && t < points.length) {
                rows[i].push(points[t++]);
            }
        });

        // Within each row, split the points into columns (collection).
        const collecs = [];
        rows.forEach((row, ri) => {
            // Sort the row by longitude.
            row = row.sort((a, b) => d3.ascending(turf.getCoord(a)[0], turf.getCoord(b)[0]));
            let p = 0;
            const c0 = collecs.length;
            for (let ci = c0; ci < c0 + row_counts[ri]; ci++) {
                collecs[ci] = [];
                while (collecs[ci].length < points_per_part && p < row.length) {
                    collecs[ci].push(row[p++]);
                }
            }
        });

        // Convert each collection of points into a Voronoi polygon,
        // but ensure that the polygon is entirely within the input polygon.
        const centroids = turf.featureCollection(collecs.map((c) => turf.centroid(turf.featureCollection(c))));
        const voronoi = turf.voronoi(
            centroids,
            { 'bbox': polygonbbox }
        );

        // Add to cache and then return the
        // indexed voronoi feature scaled back to original.
        this.splitPlot_memo[memo_key] = voronoi.features.map((vc) => {
            const mask = turf.mask(vc, turf.bboxPolygon(polygonbbox));
            const c = turf.difference(polygon, mask);
            return turf.transformScale(c, 1 / scale_factor, { 'origin': scale_origin }) as turf.helpers.Feature<turf.helpers.Polygon>;
        });

        // Return the given indexed split plot.
        return this.splitPlot_memo[memo_key][index];
    }

    private northernmost(...coordinates: [number, number][]): [number, number] {
        return coordinates.sort((a, b) => b[1] - a[1])[0];
    }

    private generateRandomString(length = 12): string {
        return Math.random().toString(36).substring(2, length + 2);
    }

    getPlotNumber(observationUnit: ObservationUnit): string {
        if (observationUnit) {
            let plotNumber = '-';
            observationUnit.observationUnitPosition.observationLevelRelationships.forEach((levels) => {
                if (this.toUpper(levels.levelName) === 'PLOT') {
                    plotNumber = levels.levelCode;
                }
            });
            return plotNumber;
        }
        return '-';
    }

    private toUpper(str: string): string {
        return (str || '').toUpperCase();
    }

}
